import { CLOSE_24 } from "@sznds/icons";
import { Unsubscribe } from "redux";
import { CONFIG } from "~/config";
import ContentOverlay from "~/content.overlay";
import { _ } from "~/i18n";
import { store } from "~/state/store";
import { IUserState } from "~/state/user/interfaces";
import { watch, watchPremiumData } from "~/state/util";
import createSDSIcon from "~/ui/icon";
import { createPrivileges } from "~/ui/premium/privileges";
import { REDEEM_20 } from "~/ui/sds-compatible-icons";
import { createPremiumIcon, node, text } from "~/util/dom";
import { onPremium } from "~/util/feedback";
import { CREDIT_CARD_20, CREDIT_CARD_GEAR_20, DISABLED_BY_DEFAULT_20, EVENT_20, EVENT_REPEAT_20, FREE_CANCELLATION_20, LOCAL_MALL_20, REPORT_20 } from "~/util/sds-compatible-icons";

export default class PremiumStatus extends ContentOverlay {
	_storeUnsubscribe: Unsubscribe;

	#closeButton: HTMLButtonElement;

	constructor() {
		super();
		this._contentName = "premium-status";
		this._dom = {
			container: node("div"),
		};
		this._dom.container.classList.add("premiumStatus");

		this.#closeButton = node("button", { type: "button", className: "premiumStatus__closeButton" });
		this.#closeButton.appendChild(createSDSIcon({ symbol: CLOSE_24 }));
		this.#closeButton.addEventListener("click", () => {
			this.deactivate();
		});
		this._buildCloseButton();
	}

	activate() {
		if (this.isActive()) {
			return this;
		}

		Mapy.getComponent("profile").activate();

		const { user } = store.getState();
		const wtUser = watch(store.getState, "user", watchPremiumData);

		this._build(user);
		this._storeUnsubscribe = store.subscribe(wtUser(newUser => {
			this._build(newUser);
		}));

		return super.activate();
	}

	deactivate() {
		if (!this.isActive()) {
			return this;
		}

		this._storeUnsubscribe?.();
		this._storeUnsubscribe = null;

		super.deactivate();
		Mapy.getComponent("history").save();

		return this;
	}

	show() {
		Mapy.getComponent("layout").setWidth(1);

		return super.show();
	}

	// eslint-disable-next-line class-methods-use-this
	getState() {
		return {};
	}

	setState(state) {
		super.setState(state);

		return this;
	}

	_build(user: IUserState) {
		this._dom.container.innerHTML = "";
		this._dom.container.appendChild(this.#closeButton);
		const subscription = user.user.premiumData?.subscriptions?.length ? window.structuredClone(user.user.premiumData.subscriptions[0]) : null;

		// tohle by nemelo nikdy nastat, ale kdyby ano, tak zkusime aspon neskoncit chybou
		if (!subscription) {
			return;
		}

		const freePremium = subscription.subscriptionOrigin === "coupon" || subscription.subscriptionOrigin === "fanda" || subscription.subscriptionOrigin === "admin" || subscription.paymentPlatform === "seznam";

		// nadpis
		const header = node("h1", { className: "premiumStatus__header" });
		const header1 = node("span", {}, _("profile.premiumStatus.title1"));
		const header2 = node("span", { className: "premiumStatus__header__premium" }, _("profile.premiumStatus.title2"));

		header2.prepend(createPremiumIcon());
		header.append(header1, header2);
		this._dom.container.appendChild(header);

		// fanda infotext
		if (freePremium && subscription.subscriptionOrigin !== "coupon") {
			this._dom.container.appendChild(node("p", { className: "premiumStatus__infoText" }, _("profile.premiumStatus.fanInfoText")));
		}

		// seznam vyhod premium
		const privilegesContainer = node("div", { className: "premiumStatus__privilegesCont" });

		privilegesContainer.appendChild(createPrivileges({
			place: "premium-status",
		}));
		this._dom.container.appendChild(privilegesContainer);

		// stav premium
		const statusContainer = node("div", { className: "premiumStatus__statusContainer" });
		const statusHeader = node("div", { className: "premiumStatus__statusHeader" });
		const statusInfo = node("div", { className: "premiumStatus__statusInfo" });
		const statusActions = node("div", { className: "premiumStatus__statusActions" });

		// hlavicka stavu
		const title = node("div", { className: "premiumStatus__statusHeader__title" });

		if (freePremium) {
			title.append(node("h2", {}, _("profile.premiumStatus.statusFreeTitle")), node("p", {}, _("profile.premiumStatus.statusFreeSubtitle")));
		} else {
			title.append(node("h2", {}, _("profile.premiumStatus.statusPaidTitle")), node("p", {}, _("profile.premiumStatus.statusPaidSubtitle")));
		}

		statusHeader.append(title, node("img", { role: "presentation", src: "/img/profile/profile-status-check.svg", className: "premiumStatus__statusHeader__image" }));

		// informace o stavu
		const statusFrom = node("div", { className: "premiumStatus__statusInfo__status" });
		const statusTo = node("div", { className: "premiumStatus__statusInfo__status" });

		if (freePremium) {
			statusFrom.append(createSDSIcon({ symbol: LOCAL_MALL_20 }), node("span", { innerHTML: _("profile.premiumStatus.statusFromFree", { start: "<strong>", end: "</strong>", date: subscription.purchaseDate ? `${new Intl.DateTimeFormat(CONFIG.defaultLang).format(new Date(subscription.purchaseDate))}` : "?" }) }));
			statusTo.append(createSDSIcon({ symbol: EVENT_20 }), node("span", { innerHTML: _("profile.premiumStatus.statusToFree", { start: "<strong>", end: "</strong>", date: subscription.expireDate ? `${new Intl.DateTimeFormat(CONFIG.defaultLang).format(new Date(subscription.expireDate))}` : "?" }) }));

			if (subscription.donorInfo) {
				const statusDonor = node("div", { className: "premiumStatus__statusInfo__status" });
				const donorSpan = node("span");

				donorSpan.append(text(`${_("premium.coupon.donor")} `), subscription.donorInfo.tocUrl
					? node("a", { href: subscription.donorInfo.tocUrl, target: "_blank" }, subscription.donorInfo.name)
					: text(subscription.donorInfo.name));
				statusDonor.append(createSDSIcon({ symbol: REDEEM_20 }), donorSpan);
				statusInfo.append(statusDonor, statusFrom, statusTo);
			} else {
				statusInfo.append(statusFrom, statusTo);
			}
		} else {
			const statusPaymentMethod = node("div", { className: "premiumStatus__statusInfo__status" });
			const paymentPlatform = subscription.paymentPlatform === "google_play"
				? "Google Play"
				: subscription.paymentPlatform === "apple_store"
					? "Apple App Store"
					: "Paddle";

			statusFrom.append(createSDSIcon({ symbol: LOCAL_MALL_20 }), node("span", { innerHTML: _("profile.premiumStatus.statusFromPaid", { start: "<strong>", end: "</strong>", date: subscription.purchaseDate ? `${new Intl.DateTimeFormat(CONFIG.defaultLang).format(new Date(subscription.purchaseDate))}` : "?" }) }));
			statusPaymentMethod.append(createSDSIcon({ symbol: CREDIT_CARD_20 }), node("span", { innerHTML: _("profile.premiumStatus.statusPaymentMethod", { method: paymentPlatform }) }));

			if (subscription.isRecurring) {
				statusTo.append(createSDSIcon({ symbol: EVENT_REPEAT_20 }), node("span", { innerHTML: _("profile.premiumStatus.statusRenewal", { start: "<strong>", end: "</strong>", date: subscription.expireDate ? `${new Intl.DateTimeFormat(CONFIG.defaultLang).format(new Date(subscription.expireDate))}` : "?" }) }));
			} else {
				statusTo.append(createSDSIcon({ symbol: EVENT_20 }), node("span", { innerHTML: _("profile.premiumStatus.statusToPaid", { start: "<strong>", end: "</strong>", date: subscription.expireDate ? `${new Intl.DateTimeFormat(CONFIG.defaultLang).format(new Date(subscription.expireDate))}` : "?" }) }));
			}

			if (subscription.isTrial && subscription.subscriptionState === "active") {
				const statusTrial = node("div", { className: "premiumStatus__statusInfo__status" });

				statusTrial.append(createSDSIcon({ symbol: FREE_CANCELLATION_20 }), node("span", { innerHTML: _("profile.premiumStatus.statusTrial", { start: "<strong>", end: "</strong>", date: subscription.expireDate ? `${new Intl.DateTimeFormat(CONFIG.defaultLang).format(new Date(subscription.expireDate))}` : "?" }) }));
				statusInfo.append(statusFrom, statusTrial, statusPaymentMethod, statusTo);
			} else {
				statusInfo.append(statusFrom, statusPaymentMethod, statusTo);
			}
		}

		// akce v ramci stavu - správa předplatného
		if (!freePremium) {
			if (subscription.managementUrls?.length) {
				subscription.managementUrls.forEach(url => {
					if (url.type === "manage" || url.type === "modify") {
						const actionManage = node("a", { href: url.url, target: "_blank", className: "premiumStatus__statusActions__action" });

						actionManage.append(createSDSIcon({ symbol: CREDIT_CARD_GEAR_20 }), node("span", {}, _("profile.premiumStatus.actionManage")));
						statusActions.appendChild(actionManage);
					}
				});
			}
		}

		const actionReport = node("button", { type: "button", className: "premiumStatus__statusActions__action" });

		actionReport.append(createSDSIcon({ symbol: REPORT_20 }), node("span", {}, _("profile.premiumStatus.actionReport")));
		statusActions.appendChild(actionReport);
		actionReport.addEventListener("click", () => {
			onPremium();
		}, false);

		// akce v ramci stavu - zrušení předplatného
		if (!freePremium && subscription.subscriptionState === "active") {
			if (subscription.managementUrls?.length) {
				subscription.managementUrls.forEach(url => {
					if (url.type === "cancel") {
						const actionManage = node("a", { href: url.url, target: "_blank", className: "premiumStatus__statusActions__action premiumStatus__statusActions__action--alert" });

						actionManage.append(createSDSIcon({ symbol: DISABLED_BY_DEFAULT_20 }), node("span", {}, _("profile.premiumStatus.actionCancel")));
						statusActions.appendChild(actionManage);
					}
				});
			}
		}

		statusContainer.append(statusHeader, statusInfo, statusActions);
		this._dom.container.appendChild(statusContainer);
	}
}
